import React, { useState } from 'react';
import { Button } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import {
  FormAccordionItem,
  FormAccordionItemContainer,
} from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { ViewField } from 'src/non-financial-data/components/fields/ViewFormField';
import { FormTable } from 'src/common/components/form-table/FormTable';
import { IconPlus } from '@tabler/icons-react';
import { formatCurrency } from 'src/common/currency';

type ContributionFormAccordionItemListProps = {
  isEditing: boolean;
  form: UseFormReturnType<FormValuesType>;
  contributionType: 'InvestmentContribution' | 'DcPensionContribution' | 'DbPensionContribution';
};

export const ContributionFormAccordionItemList = ({
  form,
  isEditing,
  contributionType,
}: ContributionFormAccordionItemListProps) => {
  const [, forceUpdate] = useState(0);

  const handleAddContribution = () => {
    form.insertListItem(contributionType, {
      id: null,
      contribution_amount: null,
      total_contributions_to_date: null,
      contribution_type: null,
      frequency: null,
      contribution_amount_type: 'CURRENCY_AMOUNT',
      start_date: null,
      end_date: null,
      source_of_funds: null,
      investment_id: null,
    });
  };

  const untypedItems = form.getValues()[contributionType];
  const items = Array.isArray(untypedItems) ? untypedItems : [];
  const totalContributions = items.reduce((total, item) => {
    const value = item.total_contributions_to_date;
    const numericValue =
      typeof value === 'number'
        ? value
        : typeof value === 'string' && !isNaN(parseFloat(value))
          ? parseFloat(value)
          : 0;
    return total + numericValue;
  }, 0);

  return (
    <FormAccordionItem value="contribution" label="Contributions" childrenWrapper={null}>
      <>
        <FormAccordionItemContainer>
          <ViewField
            fieldLabel={'Total contributions paid to date'}
            value={formatCurrency(totalContributions)}
          />
        </FormAccordionItemContainer>
        <FormAccordionItemContainer>
          <FormTable
            columns={[
              {
                accessor: 'contribution_amount',
                title: 'Amount',
              },
              {
                accessor: 'frequency',
              },
              {
                accessor: 'contribution_type',
                title: 'Type',
              },
              {
                accessor: 'start_date',
              },
              {
                accessor: 'end_date',
              },
              {
                accessor: 'total_contributions_to_date',
                title: 'Total',
                disabled: true,
                tooltip: 'This field will be calculated automatically on save',
                onValueChange: () => {
                  forceUpdate(prev => prev + 1); // needed to force a re-render to calculate totals
                },
              },
              {
                accessor: 'source_of_funds',
              },
            ]}
            dataKey={contributionType}
            form={form}
          />
        </FormAccordionItemContainer>
        {isEditing && (
          <Button
            mt={'xxl'}
            variant={'secondary'}
            onClick={() => handleAddContribution()}
            leftSection={<IconPlus size={16} />}
            data-testid="add-button"
          >
            Add contribution
          </Button>
        )}
      </>
    </FormAccordionItem>
  );
};
